'use client';
import { useResource } from '@/lib/use-resource';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, Legend,
} from 'recharts';
import type { ScoreReturnAnalysis } from '@/lib/research/score-return';

const fmtPct = (v: number, decimals = 1) => `${v >= 0 ? '+' : ''}${(v * 100).toFixed(decimals)}%`;
const fmtPp = (v: number, decimals = 1) => `${v >= 0 ? '+' : ''}${(v * 100).toFixed(decimals)}pp`;

/**
 * The headline answer to "does the Alpha Zone beat the rest of the universe?"
 *
 * Reads `data/score-return-curve.json` (forward 12m return per stock-year over
 * 8 vintages, 2017–2024). Compares the Alpha Zone (top 20 by composite each
 * year) against the Rest of Universe (everything not in the top 20 that year).
 * Both are forward-looking — the Alpha selection is made at vintage-start using
 * trailing fundamentals only, so this is genuinely out-of-sample.
 */
export function AlphaVsRestBacktest() {
  const { data: raw, loading } = useResource<ScoreReturnAnalysis | { totalObservations?: number }>('/api/score-return-curve');
  const data: ScoreReturnAnalysis | null = raw && 'totalObservations' in raw && (raw.totalObservations ?? 0) > 0
    ? (raw as ScoreReturnAnalysis)
    : null;

  if (loading) return <div className="text-[12px] text-[#64748b]">Loading Alpha vs Rest…</div>;
  if (!data) {
    return (
      <div className="text-[12px] text-[#64748b] leading-relaxed">
        Score-return curve not yet computed. Run{' '}
        <code className="text-amber-400">npx tsx scripts/analytics/compute-score-return-curve.ts</code>.
      </div>
    );
  }

  const a = data.tiers.alphaZone;
  const r = data.tiers.restOfUniverse;
  const meanEdge = a.meanReturn - r.meanReturn;
  const medianEdge = a.medianReturn - r.medianReturn;
  const sharpeEdge = a.sharpe - r.sharpe;

  // Per-year comparison — a stock-year that's in the alpha bucket counts in
  // alpha; otherwise it's in rest.
  const restByYear = new Map(r.perYear.map(p => [p.year, p]));
  const perYear = a.perYear.map(p => ({
    year: String(p.year),
    alpha: p.meanReturn,
    rest: restByYear.get(p.year)?.meanReturn ?? 0,
    edge: p.meanReturn - (restByYear.get(p.year)?.meanReturn ?? 0),
    alphaCount: p.count,
    restCount: restByYear.get(p.year)?.count ?? 0,
  }));

  const yearsPositiveEdge = perYear.filter(p => p.edge > 0).length;

  return (
    <div className="space-y-5">
      {/* Headline */}
      <div className="bg-[#0d1117] border border-amber-500/40 rounded-lg p-5">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="text-[14px] font-bold text-white tracking-wide uppercase">Alpha Zone vs Rest of Universe</div>
            <div className="text-[10px] text-[#64748b] mt-0.5">
              Forward 12m return · {data.years.length}-year sample ({data.years[0]}–{data.years[data.years.length - 1]}) ·
              vintage-start Alpha selected on trailing fundamentals (no analyst peeking)
            </div>
          </div>
          <div className="text-[10px] text-emerald-400 font-semibold">
            {yearsPositiveEdge}/{perYear.length} years positive edge
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[12px]">
          <Card
            label="Alpha Zone"
            sub={`top 20/yr (n=${a.count})`}
            mean={a.meanReturn}
            median={a.medianReturn}
            sharpe={a.sharpe}
            hitRate={a.hitRate}
            tone="good"
            highlight
          />
          <Card
            label="Rest of Universe"
            sub={`everything else (n=${r.count})`}
            mean={r.meanReturn}
            median={r.medianReturn}
            sharpe={r.sharpe}
            hitRate={r.hitRate}
            tone="neutral"
          />
          <Card
            label="Edge (Alpha − Rest)"
            sub="model premium"
            mean={meanEdge}
            median={medianEdge}
            sharpe={sharpeEdge}
            hitRate={null}
            tone={meanEdge > 0 ? 'good' : 'bad'}
            highlight
          />
        </div>
      </div>

      {/* Per-year bars */}
      <div>
        <div className="text-[10px] text-[#64748b] uppercase tracking-wide mb-2">
          Forward 12-month return — by vintage year · Alpha vs Rest
        </div>
        <div className="w-full h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={perYear} margin={{ top: 10, right: 12, bottom: 0, left: -8 }}>
              <CartesianGrid strokeDasharray="2 2" stroke="#1e293b" />
              <XAxis dataKey="year" stroke="#64748b" fontSize={10} tickLine={false} />
              <YAxis
                stroke="#64748b"
                fontSize={10}
                tickLine={false}
                tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`}
              />
              <ReferenceLine y={0} stroke="#475569" strokeWidth={1} />
              <Tooltip
                contentStyle={{ background: '#0d1117', border: '1px solid #1e293b', borderRadius: 4, fontSize: 11 }}
                labelStyle={{ color: '#94a3b8' }}
                formatter={(value, name) => {
                  if (typeof value === 'number') {
                    return [fmtPct(value), name === 'alpha' ? 'Alpha Zone' : name === 'rest' ? 'Rest' : 'Edge'];
                  }
                  return [String(value), String(name)];
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                formatter={(v: string) => v === 'alpha' ? 'Alpha Zone (top 20)' : v === 'rest' ? 'Rest of universe' : v}
              />
              <Bar dataKey="alpha" fill="#22c55e" radius={[2, 2, 0, 0]} />
              <Bar dataKey="rest" fill="#64748b" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Per-year detail table */}
      <div>
        <div className="text-[10px] text-[#64748b] uppercase tracking-wide mb-2">Per-vintage detail</div>
        <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg overflow-hidden">
          <table className="w-full text-[12px]">
            <thead className="text-[#94a3b8] border-b border-[#1e293b] text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Vintage</th>
                <th className="text-right px-3 py-2">Alpha mean</th>
                <th className="text-right px-3 py-2">Rest mean</th>
                <th className="text-right px-3 py-2">Edge (pp)</th>
                <th className="text-right px-3 py-2">Alpha n</th>
                <th className="text-right px-3 py-2">Rest n</th>
              </tr>
            </thead>
            <tbody>
              {perYear.map(p => (
                <tr key={p.year} className="border-b border-[#1e293b]/40">
                  <td className="px-3 py-1.5 font-mono text-white">{p.year}</td>
                  <td className={`text-right tabular-nums px-3 py-1.5 ${p.alpha >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{fmtPct(p.alpha)}</td>
                  <td className={`text-right tabular-nums px-3 py-1.5 ${p.rest >= 0 ? 'text-[#cbd5e1]' : 'text-orange-400'}`}>{fmtPct(p.rest)}</td>
                  <td className={`text-right tabular-nums font-bold px-3 py-1.5 ${p.edge > 0 ? 'text-emerald-400' : 'text-red-400'}`}>{fmtPp(p.edge)}</td>
                  <td className="text-right tabular-nums px-3 py-1.5 text-[#64748b]">{p.alphaCount}</td>
                  <td className="text-right tabular-nums px-3 py-1.5 text-[#64748b]">{p.restCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-[#64748b] mt-2 leading-relaxed">
          Alpha Zone = top 20 stocks per vintage year, ranked by the same composite formula used in production today.
          Selection happens at vintage-start using trailing fundamentals only — no analyst estimates, no look-ahead.
          Rest of universe = every other stock that had a valid composite + forward return that year.
        </p>
      </div>
    </div>
  );
}

function Card({
  label, sub, mean, median, sharpe, hitRate, tone, highlight = false,
}: {
  label: string;
  sub: string;
  mean: number;
  median: number;
  sharpe: number | null;
  hitRate: number | null;
  tone: 'good' | 'bad' | 'neutral';
  highlight?: boolean;
}) {
  const big = tone === 'good' ? 'text-emerald-400' : tone === 'bad' ? 'text-red-400' : 'text-[#cbd5e1]';
  const border = highlight ? 'border-amber-500/60' : tone === 'good' ? 'border-emerald-500/30' : tone === 'bad' ? 'border-red-500/30' : 'border-[#1e293b]';
  return (
    <div className={`bg-[#0a0e14] border ${border} rounded p-3`}>
      <div className="text-[10px] uppercase tracking-wide text-[#94a3b8]">{label}</div>
      <div className="text-[9px] text-[#64748b] mb-1">{sub}</div>
      <div className={`text-[24px] font-bold tabular-nums leading-none ${big}`}>
        {fmtPct(mean)}
      </div>
      <div className="text-[9px] uppercase tracking-wide text-[#64748b] mt-0.5">mean forward 12m</div>
      <div className="text-[10px] text-[#64748b] tabular-nums mt-1.5">
        median {fmtPct(median)}
        {sharpe != null && Number.isFinite(sharpe) ? <> · Sh {sharpe >= 0 ? '+' : ''}{sharpe.toFixed(2)}</> : null}
        {hitRate != null ? <> · hit {(hitRate * 100).toFixed(0)}%</> : null}
      </div>
    </div>
  );
}
